import { ref } from 'vue'

const CHECK_INTERVAL_MS = 5 * 60 * 1000 // 5 minutes
let lastChecked = 0

const isOutdated = ref(false)
const latestVersion = ref(null)
const isRefreshing = ref(false)

/** True when version a (x.y.z) is newer than b. */
function isNewer(a, b) {
    const pa = String(a).split('.').map(Number)
    const pb = String(b).split('.').map(Number)
    for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
        const diff = (pa[i] || 0) - (pb[i] || 0)
        if (diff) return diff > 0
    }
    return false
}

export function useVersionCheck() {
    const currentVersion = __APP_VERSION__
    // The build's own API, so dev builds compare against the dev version file
    const apiBase = import.meta.env.VITE_API_BASE_URL || 'https://api.snowfuze.com'

    async function checkNow() {
        if (typeof navigator !== 'undefined' && navigator.onLine === false) return
        const now = Date.now()
        if (now - lastChecked < CHECK_INTERVAL_MS) return
        lastChecked = now

        try {
            const controller = new AbortController()
            const timeoutId = setTimeout(() => controller.abort(), 8000)
            const res = await fetch(`${apiBase}/pwa-version?t=${now}`, {
                cache: 'no-store',
                signal: controller.signal
            })
            clearTimeout(timeoutId)
            if (!res.ok) return
            const data = await res.json()
            if (data.version) {
                latestVersion.value = data.version
                if (isNewer(data.version, currentVersion)) {
                    isOutdated.value = true
                }
            }
        } catch {
            // network error or timeout — silently ignore
        }
    }

    async function forceRefresh() {
        isRefreshing.value = true
        try {
            if ('serviceWorker' in navigator) {
                const registrations = await navigator.serviceWorker.getRegistrations()
                await Promise.all(registrations.map(r => r.unregister()))
            }
            if ('caches' in window) {
                const cacheNames = await caches.keys()
                await Promise.all(cacheNames.map(name => caches.delete(name)))
            }
        } catch {
            // best effort — proceed to reload regardless
        }
        window.location.reload()
    }

    return { isOutdated, currentVersion, latestVersion, checkNow, forceRefresh, isRefreshing }
}
